import getSentenceGender from 'shared/util/find-sentence-gender';
import getNestedKey from 'shared/util/get-nested-key';
import {
  InformationKeysProps,
  informationProps,
} from 'shared/util/information-props';
import {
  TranslationKeyProps,
  translatedProps,
} from 'shared/util/translated-props';
import {BaseInputProps} from '..';
import {
  AsyncMultiSelect,
  AsyncSelect,
  AudioRecorder,
  DatePicker,
  DynamicSection,
  DynamicTable,
  FileSelect,
  ImagePicker,
  Input,
  InputButton,
  ListSection,
  MultiSelect,
  Section,
  Select,
  Switch,
  TextArea,
  TextSection,
  TimePicker,
  VideoInput,
} from '../components';
import AddressInput, {AddressInputProps} from '../components/address-input';
import {AsyncMultiSelectProps} from '../components/async-multi-select';
import {AsyncSelectProps} from '../components/async-select';
import {AudioRecorderProps} from '../components/audio-recorder';
import {DatePickerProps} from '../components/date-picker';
import {DynamicSectionProps} from '../components/dynamic-section';
import {FileSelectProps} from '../components/file-select';
import InlineCode, {InlineCodeProps} from '../components/inline-code';
import {InputProps} from '../components/input';
import {InputButtonProps} from '../components/input-button';
import {ListSectionProps} from '../components/list-section';
import {MultiSelectProps} from '../components/multi-select';
import NumberInput, {NumberInputProps} from '../components/number-input';
import {SectionProps} from '../components/section';
import {SelectProps} from '../components/select';
import {SwitchProps} from '../components/switch';
import {TextAreaProps} from '../components/text-area';
import {TextSectionProps} from '../components/text-section';
import {TimePickerProps} from '../components/time-picker';
import {VideoProps} from '../components/video-input';
import {ImagePickerProps} from '../components/image-picker';
import CustomInput, {CustomInputProps} from '../components/custom-input';
import PriceInput, {PriceInputProps} from '../components/price-input';
import {DynamicTableProps} from '../components/dynamic-table';

export const handleGetInput = (
  input: BaseInputProps,
  translationKey: TranslationKeyProps,
  informationKey?: InformationKeysProps,
) => {
  if (!input.label)
    input.label = getNestedKey(
      typeof translationKey !== 'string'
        ? translationKey
        : translatedProps[translationKey as keyof typeof translatedProps],
      input.name!,
    );

  const sentenceGender = getSentenceGender(input.label ?? input.name);

  if (!input.information && informationKey)
    input.information = getNestedKey(
      typeof informationKey !== 'string'
        ? informationKey
        : informationProps[informationKey as keyof typeof informationProps],
      input.name!,
    );

  if (!input.placeholder && input.type !== 'dynamic-table')
    input.placeholder = `Informe ${sentenceGender} ${input.label.toLocaleLowerCase()}`;

  switch (input.type) {
    case 'dynamic-section':
      return (
        <DynamicSection key={input.name} {...(input as DynamicSectionProps)} />
      );
    case 'section':
      return (
        <Section
          key={input.name ?? input.label}
          {...{...(input as SectionProps), translationKey}}
        />
      );
    case 'input-button':
      return <InputButton key={input.name} {...(input as InputButtonProps)} />;
    case 'text-area':
      return <TextArea key={input.name} {...(input as TextAreaProps)} />;
    case 'select':
      return <Select key={input.name} {...(input as SelectProps)} />;
    case 'multi-select':
      return <MultiSelect key={input.name} {...(input as MultiSelectProps)} />;
    case 'date-picker':
      return <DatePicker key={input.name} {...(input as DatePickerProps)} />;
    case 'time-picker':
      return <TimePicker key={input.name} {...(input as TimePickerProps)} />;
    case 'address-input':
      return (
        <AddressInput key={input.name} {...(input as AddressInputProps)} />
      );
    case 'file-select':
      return <FileSelect key={input.name} {...(input as FileSelectProps)} />;
    case 'switch':
      return <Switch key={input.name} {...(input as SwitchProps)} />;
    case 'async-select':
      return <AsyncSelect key={input.name} {...(input as AsyncSelectProps)} />;
    case 'number-input':
      return <NumberInput key={input.name} {...(input as NumberInputProps)} />;
    case 'audio-recorder':
      return (
        <AudioRecorder key={input.name} {...(input as AudioRecorderProps)} />
      );
    case 'list-section':
      return <ListSection key={input.name} {...(input as ListSectionProps)} />;
    case 'video-input':
      return <VideoInput key={input.name} {...(input as VideoProps)} />;
    case 'async-multi-select':
      return (
        <AsyncMultiSelect
          key={input.name}
          {...(input as AsyncMultiSelectProps)}
        />
      );
    case 'text-section':
      return <TextSection key={input.name} {...(input as TextSectionProps)} />;
    case 'inline-code':
      return <InlineCode key={input.name} {...(input as InlineCodeProps)} />;
    case 'image-picker':
      return <ImagePicker key={input.name} {...(input as ImagePickerProps)} />;
    case 'custom':
      return <CustomInput key={input.name} {...(input as CustomInputProps)} />;
    case 'price-input':
      return <PriceInput key={input.name} {...(input as PriceInputProps)} />;
    case 'dynamic-table':
      return (
        <DynamicTable key={input.name} {...(input as DynamicTableProps)} />
      );
    default:
      return <Input key={input.name} {...(input as InputProps)} />;
  }
};
